// file: client/src/offline/localStore.js
/**
 * MODULE 09 — the local reference database (offline-first read source).
 *
 * Primed at enrollment AND at the first session of each day (the "download the
 * required database" requirement), then kept continuously fresh by delta
 * cursors while online (the "maintain synchronization" requirement). The
 * server stays the brain at replay — these projections exist for continuity.
 */
import apiClient from "../services/api.client.js";
import { idbBulkPut, idbAll, idbGet, idbCount, metaGet, metaSet } from "./idb.js";
import { useOfflineStore } from "./offlineStore.js";
// Refonte WP-13 (A-11 « same algorithm offline ») — the server's matcher, byte for byte.
import { rechercherDans, preparerRequete } from "@pharmakon/shared/search";
// Cross-module XWP-25 — the counters' code resolver, answered from the snapshot.
import { resoudreCodeHorsLigne } from "./resolution-code.js";

// The reference stores primed/refreshed (server `/sync/deltas/:store`).
const STORES = ["produits", "stock", "clients", "medecins"];

/** Local calendar date key (YYYY-MM-DD) for the daily-prime gate. */
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Pull one store's delta pages until exhausted (cursor-resumable). */
async function pullStore(store, onPage) {
  let cursor = await metaGet(`cursor:${store}`);
  for (;;) {
    const res = await apiClient.get(`/sync/deltas/${store}`, {
      params: cursor ? { since: cursor } : {},
      // Deltas must hit the network, never the offline cache (would loop).
      _bypassOfflineGate: true,
    });
    const { rows, cursor: next } = res.data;
    if (!rows?.length) break;
    await idbBulkPut(`ref_${store}`, rows);
    cursor = next;
    await metaSet(`cursor:${store}`, cursor);
    onPage?.(rows.length);
    if (rows.length < 500) break; // last page
  }
}

/** Full prime with progress (enrollment wizard + the daily refresh). */
export async function primeAll() {
  const st = useOfflineStore.getState();
  st.set({ priming: { step: STORES[0], done: 0, total: STORES.length } });
  let i = 0;
  for (const store of STORES) {
    st.set({ priming: { step: store, done: i, total: STORES.length } });
    await pullStore(store);
    i += 1;
  }
  const now = new Date().toISOString();
  await metaSet("primed_at", now);
  await metaSet("last_refresh_at", now);
  await metaSet("last_full_prime_date", todayKey());
  st.set({ priming: null, primed: true, lastPrimeAt: now });
  try {
    await navigator.storage?.persist?.();
  } catch {
    /* unsupported — surfaced in Paramètres */
  }
}

/**
 * Daily prime — the "first session of each day, download the database" rule.
 * Runs a full prime when the last full prime was on a different calendar day
 * (or never). No-op the rest of the day; deltas keep it fresh meanwhile.
 */
export async function dailyPrimeIfNeeded() {
  const last = await metaGet("last_full_prime_date");
  if (last === todayKey() && (await isPrimed())) return false;
  await primeAll();
  return true;
}

/** Incremental refresh — keeps local in step with the server during the day. */
export async function refreshDeltas() {
  for (const store of STORES) await pullStore(store);
  await metaSet("last_refresh_at", new Date().toISOString());
  useOfflineStore.getState().set({ lastRefreshAt: new Date().toISOString() });
}

export async function cacheAgeHours() {
  const iso = (await metaGet("last_refresh_at")) ?? (await metaGet("primed_at"));
  if (!iso) return null;
  return (Date.now() - new Date(iso).getTime()) / 3600000;
}

export async function isPrimed() {
  return Boolean(await metaGet("primed_at")) && (await idbCount("ref_produits")) > 0;
}

// ─── Read projections (consumed by the OfflineGate) ──────────────────────────

/**
 * Cross-module XWP-03 — the sellable units of a snapshot lot: the server ships `vendable`
 * (non-expired, outside blocks and caisse holds) and `visible_caissier` (placement authority);
 * rows primed before XWP-03 fall back to the on-hand quantity and the lot's home magasin.
 */
const vendableLot = (l) => Number(l.vendable ?? l.quantite_disponible ?? 0) || 0;
const visibleLot = (l) =>
  l.visible_caissier ?? (l.magasin_id == null || l.magasin?.visible_caissier !== false);

const norm = (s) =>
  (s ?? "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

/** Offline order inside a relevance tier — the caisse rule of ux/04 §3.1 (sellable first). */
const RANG_DISPONIBILITE = Object.freeze({ VISIBLE: 0, BLOQUE: 1, RUPTURE: 2 });

/**
 * posSearch projection — tiers + qte_totale + FEFO head + CHIFA fields. Refonte WP-13: the shared
 * matcher (token-AND in any order over every field, accents / case folded, EXACT › prefix › substring
 * › « Orthographe proche ») on the snapshot's `search_text` (rebuilt from the row when a pre-M-08
 * snapshot carries none); the caisse tie-break (sellable first) inside a tier, then A→Z.
 */
export async function searchProduits(q, limit = 15) {
  const requete = preparerRequete(q);
  if (!requete.valide) return [];
  const [produits, lots] = await Promise.all([
    idbAll("ref_produits"),
    idbAll("ref_stock"),
  ]);
  const lotsByProduit = new Map();
  for (const l of lots) {
    if (l.deleted_at || vendableLot(l) <= 0) continue;
    const arr = lotsByProduit.get(l.produit_id) ?? [];
    arr.push(l);
    lotsByProduit.set(l.produit_id, arr);
  }
  const lignes = [];
  for (const p of produits) {
    if (p.deleted_at) continue;
    if (p.statut_produit === "BLOQUE_CACHE") continue;
    const plots = (lotsByProduit.get(p.id) ?? [])
      .filter(visibleLot)
      .sort((a, b) => new Date(a.date_peremption) - new Date(b.date_peremption));
    const qte_totale = plots.reduce((s, l) => s + vendableLot(l), 0);
    lignes.push({
      ...p,
      qte_totale,
      disponibilite:
        p.statut_produit === "BLOQUE_VISIBLE"
          ? "BLOQUE"
          : qte_totale > 0
            ? "VISIBLE"
            : "RUPTURE",
      lot_fefo: plots[0] ?? null,
      _offline: true,
    });
  }
  return rechercherDans(lignes, requete, {
    limite: limit,
    departager: (a, b) => (RANG_DISPONIBILITE[a.disponibilite] ?? 9) - (RANG_DISPONIBILITE[b.disponibilite] ?? 9),
  });
}

/**
 * Full produit list (paginated), for /produits offline. Refonte WP-13 (AUD-SRCH-19): a `search`
 * filters and ranks with the shared matcher (it was ignored); without one, A→Z.
 */
export async function listProduits({ page = 1, limit = 50, search } = {}) {
  const vivants = (await idbAll("ref_produits")).filter((p) => !p.deleted_at);
  const requete = search ? preparerRequete(search) : null;
  const all = requete?.valide
    ? rechercherDans(vivants, requete)
    : vivants.sort((a, b) => norm(a.nom_commercial).localeCompare(norm(b.nom_commercial)));
  const start = (page - 1) * limit;
  return { rows: all.slice(start, start + limit), total: all.length };
}

/**
 * Cross-module XWP-25 — `GET /codes/resolve` offline: a scanned lot label or box barcode → the same
 * answer shape as the server, from the synced codes (lot `code_lot`, produit `codes_barre[]`).
 */
export async function resoudreCode(code, canal) {
  const [produits, lots] = await Promise.all([idbAll("ref_produits"), idbAll("ref_stock")]);
  return resoudreCodeHorsLigne({ code, canal, produits, lots });
}

export async function getProduit(id) {
  return idbGet("ref_produits", id);
}

export async function listLots(produit_id) {
  const lots = await idbAll("ref_stock");
  return lots
    .filter(
      (l) =>
        (!produit_id || l.produit_id === produit_id) &&
        !l.deleted_at &&
        vendableLot(l) > 0,
    )
    .sort((a, b) => new Date(a.date_peremption) - new Date(b.date_peremption));
}

export async function searchClients(q, limit = 50) {
  const needle = norm(q);
  const clients = (await idbAll("ref_clients")).filter((c) => !c.deleted_at);
  const matched = needle
    ? clients.filter((c) =>
        `${norm(c.nom)} ${norm(c.prenom)} ${c.telephone ?? ""} ${c.cin ?? ""}`.includes(needle),
      )
    : clients;
  return matched.slice(0, limit);
}

export async function getClient(id) {
  return idbGet("ref_clients", id);
}

export async function listMedecins(q, limit = 50) {
  const needle = norm(q);
  const meds = (await idbAll("ref_medecins")).filter((m) => !m.deleted_at);
  const matched = needle
    ? meds.filter((m) => `${norm(m.nom)} ${norm(m.prenom)}`.includes(needle))
    : meds;
  return matched.slice(0, limit);
}

/**
 * Optimistic local FEFO decrement after an offline sale — keeps search/stock
 * views consistent during the offline session. The authoritative figures are
 * rebuilt from server deltas after sync.
 */
export async function applyLocalDeduction(details) {
  const lots = await idbAll("ref_stock");
  const byId = new Map(lots.map((l) => [l.id, l]));
  const touched = [];
  for (const d of details) {
    let remaining = d.quantite ?? 0;
    const plots = lots
      .filter((l) => l.produit_id === d.produit_id && vendableLot(l) > 0)
      .sort((a, b) => new Date(a.date_peremption) - new Date(b.date_peremption));
    for (const l of plots) {
      if (remaining <= 0) break;
      const take = Math.min(remaining, vendableLot(l));
      const row = byId.get(l.id);
      row.quantite_disponible -= take;
      if (row.vendable != null) row.vendable -= take;
      remaining -= take;
      touched.push(row);
    }
  }
  if (touched.length) await idbBulkPut("ref_stock", touched);
}
